import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { useGetFetchNew } from "../../data/Queries";
import { commonPostPutApi } from "../../redux/reducers/commonApiSlice";
import CommonHeadingNBtns from "../common-components/CommonHeadingNBtns";
import { EditIcon, DeleteIcon } from "../../images/icons/website-icons";
import { formatDate } from "../../utils/utilityfunc";
import { showConfirmAlert } from "../components-react/ReactAlert";
import { departmentSchema } from "../../schema/website-schema";
import {
  add_team_department,
  delete_team_department,
  get_team_departments,
  update_team_department,
} from "../../data/ApiEndpoints";
import "../../styles/website/management-forms.css";

const ManageTeamDepartment = ({ closeModal, token, getDepartmentData }) => {
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [departments, setDepartments] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Fetch departments
  const {
    data,
    isLoading: isLoadingDepartments,
    refetch,
  } = useGetFetchNew({
    token,
    key: "team-departments",
    url: get_team_departments,
  });

  useEffect(() => {
    if (data?.data) {
      setDepartments(data.data);
    }
  }, [data]);

  const formik = useFormik({
    initialValues: {
      title: "",
      description: "",
      seq_no: "",
    },
    validationSchema: departmentSchema,
    onSubmit: async (values) => {
      await handleSubmit(values);
    },
  });

  // Select department for editing
  const handleEdit = (department) => {
    setSelectedDepartment(department);
    setEditMode(true);
    formik.setValues({
      title: department.title || "",
      description: department.description || "",
      seq_no: department.seq_no || "",
    });
  };

  // Reset form and exit edit mode
  const handleCancel = () => {
    setEditMode(false);
    setSelectedDepartment(null);
    formik.resetForm();
  };

  // Handle form submission (add or update)
  const handleSubmit = async (values) => {
    setIsLoading(true);
    try {
      const method = editMode ? "PUT" : "POST";
      const endpoint = editMode
        ? `${update_team_department}/${selectedDepartment.uid}`
        : add_team_department;

      // Prepare payload
      const payload = { ...values };

      // Add UID for edit operations if it's not already in the endpoint
      if (editMode && selectedDepartment?.uid) {
        payload.uid = selectedDepartment.uid;
      }

      console.log(`${editMode ? "Updating" : "Creating"} department:`, payload);

      const result = await dispatch(
        commonPostPutApi({
          method,
          endpoint,
          payload,
          token,
        })
      );

      if (result?.payload?.status) {
        toast.success(
          result?.payload?.message ||
            `Department ${editMode ? "updated" : "created"} successfully!`
        );
        formik.resetForm();
        setEditMode(false);
        setSelectedDepartment(null);
        refetch();
        getDepartmentData && getDepartmentData();
      } else {
        const errorMessage = result?.error?.message || result?.payload?.message;
        toast.error(errorMessage || "Something went wrong!");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle department deletion
  const handleDelete = async (uid) => {
    const handleConfirm = async () => {
      setIsLoading(true);
      try {
        const result = await dispatch(
          commonPostPutApi({
            method: "DELETE",
            endpoint: `${delete_team_department}/${uid}`,
            token,
          })
        );

        if (result?.payload?.status) {
          toast.success(
            result?.payload?.message || "Department deleted successfully!"
          );
          refetch();
          getDepartmentData && getDepartmentData();
        } else {
          const errorMessage =
            result?.error?.message || result?.payload?.message;
          toast.error(errorMessage || "Failed to delete department!");
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Something went wrong!");
      } finally {
        setIsLoading(false);
      }
    };

    showConfirmAlert(
      handleConfirm,
      "Are you sure you want to delete this department?"
    );
  };

  return (
    <div className="createNewCTOModalContainer mod-width-70p">
      <CommonHeadingNBtns
        title="Manage Departments"
        onClose={closeModal}
        btnTxt={null} // No button in header
      />

      <div className="p-4">
        <div className="row">
          {/* Department Form */}
          <div className="col-md-5">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-3">
                  {editMode ? "Update Department" : "Add New Department"}
                </h5>

                <form
                  onSubmit={formik.handleSubmit}
                  className="management-form-container"
                >
                  <div className="management-form-elements">
                    {/* Sequence Number */}
                    <div className="management-form-group">
                      <label
                        htmlFor="seq_no"
                        className={`management-form-label ${
                          formik.touched.seq_no && formik.errors.seq_no
                            ? "error"
                            : ""
                        }`}
                      >
                        Sequence Number{" "}
                        <span className="required-indicator">*</span>
                      </label>
                      <input
                        id="seq_no"
                        name="seq_no"
                        type="number"
                        className={`management-form-input ${
                          formik.touched.seq_no && formik.errors.seq_no
                            ? "error"
                            : ""
                        }`}
                        placeholder="Enter sequence number"
                        value={formik.values.seq_no}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.seq_no && formik.errors.seq_no && (
                        <div className="management-form-error">
                          {formik.errors.seq_no}
                        </div>
                      )}
                    </div>

                    {/* Title */}
                    <div className="management-form-group">
                      <label
                        htmlFor="title"
                        className={`management-form-label ${
                          formik.touched.title && formik.errors.title
                            ? "error"
                            : ""
                        }`}
                      >
                        Title <span className="required-indicator">*</span>
                      </label>
                      <input
                        id="title"
                        name="title"
                        type="text"
                        className={`management-form-input ${
                          formik.touched.title && formik.errors.title
                            ? "error"
                            : ""
                        }`}
                        placeholder="Enter department title"
                        value={formik.values.title}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.title && formik.errors.title && (
                        <div className="management-form-error">
                          {formik.errors.title}
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    <div className="management-form-group">
                      <label
                        htmlFor="description"
                        className={`management-form-label ${
                          formik.touched.description &&
                          formik.errors.description
                            ? "error"
                            : ""
                        }`}
                      >
                        Description{" "}
                        <span className="required-indicator">*</span>
                      </label>
                      <textarea
                        id="description"
                        name="description"
                        className={`management-form-textarea ${
                          formik.touched.description &&
                          formik.errors.description
                            ? "error"
                            : ""
                        }`}
                        placeholder="Enter department description"
                        rows="3"
                        value={formik.values.description}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                      />
                      {formik.touched.description &&
                        formik.errors.description && (
                          <div className="management-form-error">
                            {formik.errors.description}
                          </div>
                        )}
                    </div>

                    {/* Action Buttons */}
                    <div className="d-flex justify-content-end">
                      {editMode && (
                        <button
                          type="button"
                          className="btn btn-outline-secondary me-2"
                          onClick={handleCancel}
                        >
                          Cancel
                        </button>
                      )}
                      <button
                        type="submit"
                        className="btn btn-danger"
                        disabled={isLoading}
                      >
                        {isLoading
                          ? "Processing..."
                          : editMode
                          ? "Update Department"
                          : "Add Department"}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>

          {/* Departments List */}
          <div className="col-md-7">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-3">All Departments</h5>

                {isLoadingDepartments ? (
                  <div className="text-center my-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : departments.length === 0 ? (
                  <div className="alert alert-info">
                    No departments found. Add your first department!
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Sequence</th>
                          <th>Title</th>
                          <th>Date Added</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {departments.map((dept) => (
                          <tr key={dept.uid}>
                            <td>{dept.seq_no}</td>
                            <td>{dept.title}</td>
                            <td>{formatDate(dept.date_added)}</td>
                            <td>
                              <div className="d-flex">
                                <button
                                  className="btn btn-sm btn-outline-danger me-2"
                                  onClick={() => handleEdit(dept)}
                                >
                                  <EditIcon />
                                </button>
                                <button
                                  className="btn btn-sm btn-outline-danger"
                                  onClick={() => handleDelete(dept.uid)}
                                  disabled={isLoading}
                                >
                                  <DeleteIcon />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ManageTeamDepartment;
